import React from 'react'
import MultiSelect from '../../components/MultiSelect'
import { Grade, Class, School, Race, Gender } from '../../../types'

interface StudentGeneralTabProps {
  formData: {
    studentId: string
    firstName: string
    lastName: string
    grade: string
    race: string
    gender: string
    caseManager: string
  }
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => void
  selectedSchool: string
  handleSchoolChange: (schoolId: string) => void
  selectedClasses: string[]
  handleClassChange: (selectedIds: string[]) => void
  schools: School[]
  schoolClasses: Class[]
}

const StudentGeneralTab: React.FC<StudentGeneralTabProps> = ({
  formData,
  handleInputChange,
  selectedSchool,
  handleSchoolChange,
  selectedClasses,
  handleClassChange,
  schools,
  schoolClasses,
}) => {
  const getAvailableClasses = () => {
    if (!selectedSchool) return []

    return schoolClasses
      .filter((cls: Class) => {
        const classSchoolId =
          typeof cls.school === 'object' ? cls.school._id : cls.school
        return classSchoolId === selectedSchool
      })
      .map((cls: Class) => ({
        _id: cls._id,
        name: cls.name,
      }))
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            placeholder="Enter first name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            autoFocus
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            placeholder="Enter last name"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Student ID (Optional)
          </label>
          <input
            type="text"
            name="studentId"
            value={formData.studentId}
            onChange={handleInputChange}
            placeholder="Enter student ID"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Unique identifier for this student
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Grade
          </label>
          <select
            name="grade"
            value={formData.grade}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select grade</option>
            {Object.values(Grade).map((grade) => (
              <option key={grade} value={grade}>
                {grade}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            School
          </label>
          <select
            value={selectedSchool}
            onChange={(e) => handleSchoolChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a school</option>
            {schools.map((school: School) => (
              <option key={school._id} value={school._id}>
                {school.displayName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Race (Optional)
          </label>
          <select
            name="race"
            value={formData.race}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select race</option>
            {Object.values(Race).map((race) => (
              <option key={race} value={race}>
                {race}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Gender (Optional)
          </label>
          <select
            name="gender"
            value={formData.gender}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select gender</option>
            {Object.values(Gender).map((gender) => (
              <option key={gender} value={gender}>
                {gender}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Case Manager (Optional)
        </label>
        <input
          type="text"
          name="caseManager"
          value={formData.caseManager}
          onChange={handleInputChange}
          placeholder="Enter case manager name"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Classes
        </label>
        <MultiSelect
          options={getAvailableClasses()}
          selectedValues={selectedClasses}
          onChange={handleClassChange}
          placeholder="Select classes for this student..."
          className="w-full"
          fixed={true}
        />
        <p className="mt-1 text-xs text-gray-500">
          {selectedSchool
            ? 'Select which classes this student should be enrolled in'
            : 'Please select a school first to see available classes'}
        </p>
      </div>
    </div>
  )
}

export default StudentGeneralTab
